import { useEffect, useRef, useState } from 'react'
import type { ScheduleEntry } from '@myapp/contract'
import {
  useAddAppointmentTechnicianAttachments,
  useCompleteAppointment,
} from '../../../entities/appointment'
import Modal from '../../../shared/ui/overlay/Modal/Modal'
import { getStartWorkErrorMessage } from '../model/getStartWorkErrorMessage'
import {
  changedFields,
  nowInstant,
  sessionTimesFrom,
  type SessionTimes,
} from '../model/workSessionTime'
import { SessionTimesDisclosure } from './SessionTimesDisclosure'
import { WorkTimeSummary } from './WorkTimeSummary'
import { CommentDisclosure, commentPatch } from './CommentDisclosure'
import { FilesDisclosure } from './FilesDisclosure'

/** The three instants the sign-off reviews. The departure is not one — it happens after. */
const EDITABLE: Array<keyof SessionTimes> = [
  'travelStartedAt',
  'actualWorkStartsAt',
  'actualEndsAt',
]

type Props = {
  show: boolean
  setShow: (show: boolean) => void
  appointmentId: number
  workOrderId?: string
  schedule: ScheduleEntry
  /** What the worker already wrote during the job — prefilled, so the sheet edits rather than replaces. */
  technicianComment?: string | null
  onSuccess: () => void
}

/**
 * "Arbeit abschließen" — the sign-off, and the one moment the worker may correct this session's
 * instants. Plain confirm is a single tap; the correction is collapsed behind "Zeiten anpassen",
 * the comment behind "Kommentar", the files behind "Dateien hinzufügen".
 *
 * Files go up FIRST, on their own route, then the sign-off. Two requests rather than one
 * multipart sign-off: the files are the same thing the detail-page card sends, and a sign-off
 * rejected for its times must not lose the photos — they are already on the appointment and the
 * retry skips the upload (`uploadedRef`).
 *
 * Safe for the live state precisely because you cannot reach this sheet without having walked
 * the phases for real: it reviews the record, it cannot replace the taps.
 */
export const CompleteAppointmentModal = ({
  show,
  setShow,
  appointmentId,
  workOrderId,
  schedule,
  technicianComment,
  onSuccess,
}: Props) => {
  const complete = useCompleteAppointment()
  const addAttachments = useAddAppointmentTechnicianAttachments()
  const [times, setTimes] = useState<SessionTimes | null>(null)
  const [initial, setInitial] = useState<SessionTimes | null>(null)
  const [showTimes, setShowTimes] = useState(false)
  const [showComment, setShowComment] = useState(false)
  const [showFiles, setShowFiles] = useState(false)
  const [comment, setComment] = useState('')
  const [files, setFiles] = useState<File[]>([])
  const [submitError, setSubmitError] = useState<string | null>(null)
  const uploadedRef = useRef(false)

  useEffect(() => {
    if (!show) return
    complete.reset()
    addAttachments.reset()
    const start = sessionTimesFrom(schedule, { field: 'actualEndsAt', value: nowInstant() })
    setInitial(start)
    setTimes(start)
    setShowTimes(false)
    // An existing comment opens its disclosure: hiding text the worker wrote an hour ago behind a
    // collapsed "Kommentar" would read as "gone".
    setComment(technicianComment ?? '')
    setShowComment(Boolean(technicianComment))
    setShowFiles(false)
    setFiles([])
    setSubmitError(null)
    uploadedRef.current = false
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [show, schedule.actualWorkStartsAt])

  const isPending = complete.isPending || addAttachments.isPending

  const onSubmit = async () => {
    if (!times || !initial || isPending) return
    setSubmitError(null)
    try {
      if (files.length && !uploadedRef.current) {
        await addAttachments.mutateAsync({ id: appointmentId, workOrderId, files })
        uploadedRef.current = true
        setFiles([])
      }
      await complete.mutateAsync({
        id: appointmentId,
        workOrderId,
        dto: {
          ...changedFields(initial, times, EDITABLE),
          // Only when it changed — a plain confirm sends nothing, like the times. The sheet holds
          // the stored text, so an emptied field is a deliberate clearing and goes out as "".
          ...commentPatch(technicianComment, comment),
        },
      })
      onSuccess()
    } catch (error) {
      setSubmitError(getStartWorkErrorMessage(error))
    }
  }

  return (
    <Modal
      show={show}
      setShow={setShow}
      title="Arbeit abschließen"
      submitButton={{ title: 'Arbeit abschließen', isPending, onSubmit }}
    >
      <div className="flex w-full flex-col gap-4">
        <WorkTimeSummary actualStart={schedule.actualWorkStartsAt} />

        {times && (
          <SessionTimesDisclosure
            times={times}
            editable={EDITABLE}
            onChange={setTimes}
            open={showTimes}
            onToggle={() => setShowTimes((v) => !v)}
          />
        )}

        <CommentDisclosure
          value={comment}
          onChange={setComment}
          open={showComment}
          onToggle={() => setShowComment((v) => !v)}
        />

        <FilesDisclosure
          files={files}
          onChange={setFiles}
          open={showFiles}
          onToggle={() => setShowFiles((v) => !v)}
        />

        {submitError && (
          <p role="alert" className="text-sm font-medium text-red-500">
            {submitError}
          </p>
        )}
      </div>
    </Modal>
  )
}
